import { describeOverShort } from '@didto/domain';
import {
  Badge,
  Button,
  EmptyState,
  KvRow,
  Loading,
  Notice,
  PageHeader,
  SectionHead,
  Surface,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useOwnerReview } from '@/app/ownerReview';
import { useSession } from '@/app/session';
import { useToast } from '@/app/toast';
import { useOnline } from '@/lib/network';
import { CLOSE_KIND_LABEL, fetchShiftDetail, reviewKeys } from '@/lib/review';
import { LEDGER_ROW_TONE } from '@/lib/status';
import { ACTION_LABEL, ShiftSummaryView } from '@/routes/pos/shiftSummary';
import {
  CorrectionsSection,
  PaymentsSection,
  categoryLabel,
  type CorrectionTarget,
} from './corrections';
import {
  NoncashRows,
  ReadError,
  ReviewItemCard,
  ReviewStatusBadge,
  useFormats,
} from './reviewItems';

const MOVEMENT_LABEL: Record<string, string> = {
  pay_in: 'Pay-in',
  pay_out: 'Pay-out',
  safe_drop: 'Safe drop',
  no_sale: 'No sale',
  removal: 'Taken out',
  void: 'Cancelled',
};

/** One shift: what the close froze and what the rows say now, its items, its non-cash rows, its history. */
export function ShiftDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { run, ensureFresh } = useOwnerReview();
  const s = useSession();
  const f = useFormats();
  const online = useOnline();
  const { toast } = useToast();
  const [target, setTarget] = useState<CorrectionTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const q = useQuery({
    queryKey: reviewKeys.shift(id),
    enabled: !!id,
    queryFn: () => fetchShiftDetail(id),
    refetchInterval: 60_000,
  });
  const d = q.data;
  const mark = async () => {
    if (!d) return;
    setBusy(true);
    try {
      if (!(await ensureFresh())) return;
      const r = await run('shift.mark_reviewed', { shift_id: d.shift.id, digest: d.review.digest });
      if (r) toast('Marked reviewed');
    } finally {
      setBusy(false);
    }
  };
  if (!d)
    return (
      <div className="d-page">
        {q.error ? (
          <ReadError
            error={q.error}
            hasData={false}
            onRetry={() => void q.refetch()}
            testId="shift-error"
          />
        ) : (
          <Loading label="Loading the shift" rows={4} />
        )}
      </div>
    );
  const sh = d.shift;
  const red = d.review.items.filter((i) => i.tier === 'red' && i.ack?.state !== 'closed');
  const counted = d.review.items.filter((i) => i.tier === 'counted');
  const canMark =
    sh.status === 'closed' && d.review.red_open === 0 && d.review.status !== 'reviewed';
  const titles = new Map(d.review.items.map((i) => [i.item_key, i.title]));
  return (
    <div className="d-page" data-testid="shift-detail" data-status={d.review.status}>
      <PageHeader
        eyebrow={`Owner · ${f.date(sh.business_date)}`}
        title={`#${sh.seq} · ${sh.drawer?.name ?? 'Drawer'} · ${sh.cashier.name}`}
        subtitle={`${f.at(sh.opened_at)}${sh.closed_at ? ` → ${f.at(sh.closed_at)}` : ' · open'}${
          sh.close_kind ? ` · ${CLOSE_KIND_LABEL[sh.close_kind] ?? sh.close_kind}` : ''
        }${sh.closed_by && sh.closed_by.id !== sh.cashier.id ? ` (${sh.closed_by.name})` : ''}`}
        actions={
          <>
            <ReviewStatusBadge status={sh.status === 'closed' ? d.review.status : 'open'} />
            <Link
              to={`/shifts/day/${sh.business_date}`}
              className="d-btn d-btn--sm"
              data-testid="to-day"
            >
              Day view
            </Link>
          </>
        }
      />
      <ReadError error={q.error} hasData onRetry={() => void q.refetch()} testId="shift-error" />
      {d.review.reviewed_at && (
        <p className="d-help" data-testid="reviewed-by">
          Marked reviewed {f.at(d.review.reviewed_at)} by {d.review.reviewed_by?.name}
          {d.review.reviewed_via === 'day' ? ' (with the day)' : ''}
          {d.review.status === 'changed' ? ' · changed since' : ''}
        </p>
      )}
      {d.adjusted.corrected && d.adjusted.over_short_minor != null && (
        <Notice tone="info" testId="adjusted-notice">
          As corrected: {describeOverShort(d.adjusted.over_short_minor, f.money)}
          {sh.over_short_minor != null && sh.over_short_minor !== d.adjusted.over_short_minor
            ? ` (closed ${describeOverShort(sh.over_short_minor, f.money).toLowerCase()})`
            : ''}
          {d.adjusted.settled_minor > 0
            ? ` · ${f.money(d.adjusted.settled_minor)} settled${
                d.adjusted.unsettled_minor > 0
                  ? `, ${f.money(d.adjusted.unsettled_minor)} still unsettled`
                  : ''
              }`
            : ''}
          {d.adjusted.over_settled_minor > 0 &&
          d.adjusted.unsettled_minor === 0 &&
          d.adjusted.settled_minor > 0
            ? ` · ${f.money(d.adjusted.over_settled_minor)} settled beyond the difference`
            : ''}
        </Notice>
      )}

      <section className="rv-section" aria-label="To check">
        <SectionHead title="To check" />
        <div className="d-list" data-testid="shift-items">
          {red.map((i) => (
            <ReviewItemCard key={i.item_key} item={i} linkShift={false} />
          ))}
          {red.length === 0 && <EmptyState title="Nothing to check on this shift." />}
        </div>
        {counted.length > 0 && (
          <Surface pad="sm">
            <section className="d-kv" aria-label="Counted" data-testid="shift-counted">
              <div className="d-eyebrow">Counted</div>
              {counted.map((i) => (
                <KvRow
                  key={i.item_key}
                  label={`${i.title}${typeof i.facts.count === 'number' ? ` (${String(i.facts.count)})` : ''}`}
                  value={
                    <span className="d-mono">
                      {i.amount_minor != null ? f.money(i.amount_minor) : ''}
                    </span>
                  }
                />
              ))}
            </section>
          </Surface>
        )}
        {canMark && (
          <div className="rv-foot">
            <Button
              variant="primary"
              busy={busy}
              disabled={!online}
              onClick={() => void mark()}
              data-testid="mark-reviewed"
            >
              Mark reviewed
            </Button>
          </div>
        )}
      </section>

      <div className="rv-pair">
        <section className="rv-section" aria-label="Summary">
          <SectionHead title="Summary" />
          <Surface>
            <div className="d-stack">
              {d.now.expected_differs && d.frozen && (
                <Notice tone="warning" testId="expected-moved">
                  Expected cash at the close was {f.money(d.frozen.cash.expected_minor)}; the rows
                  now say {f.money(d.now.expected_now_minor)}
                  {d.now.late_entries > 0
                    ? ` (${d.now.late_entries} late ${d.now.late_entries === 1 ? 'entry' : 'entries'})`
                    : ''}
                  .
                </Notice>
              )}
              <ShiftSummaryView
                summary={d.frozen ?? d.live}
                live={d.frozen ? d.live : null}
                currency={s.me?.business?.currency ?? 'PHP'}
                timeZone={s.me?.business?.timezone ?? 'Asia/Manila'}
              />
            </div>
          </Surface>
        </section>
        <section className="rv-section" aria-label="Cash entries">
          <SectionHead title="Cash entries" />
          <div className="d-list" data-testid="shift-movements">
            {d.movements.map((m) => (
              <div key={m.id} className="d-row rv-row--compact">
                <div className="d-row__main">
                  <div className="d-row__title">
                    {MOVEMENT_LABEL[m.kind] ?? m.kind}
                    {m.category ? ` · ${categoryLabel(m.category)}` : ''}
                    {m.payee ? ` · ${m.payee}` : ''}
                    {m.removed_to ? ` · to the ${m.removed_to}` : ''}
                  </div>
                  <div className="d-row__meta">
                    {f.at(m.recorded_at)} · {m.by.name}
                    {m.approved_by ? ` · approved by ${m.approved_by.name}` : ''}
                    {m.reason ? ` · “${m.reason}”` : ''}
                    {(m.effective?.flags ?? m.flags).over_threshold ? ' · over the limit' : ''}
                    {(m.effective?.flags ?? m.flags).cashier_day_over
                      ? ' · over the day limit'
                      : ''}
                    {(m.effective?.flags ?? m.flags).no_receipt ? ' · no receipt' : ''}
                  </div>
                </div>
                <div className="rv-side">
                  <span className="rv-money" data-testid="movement-amount">
                    {m.effective && m.effective.amount_minor !== m.amount_minor ? (
                      <>
                        <s className="d-faint">{f.money(m.amount_minor)}</s>{' '}
                        {f.money(m.effective.amount_minor)}
                      </>
                    ) : (
                      f.money(m.amount_minor)
                    )}
                  </span>
                  {m.voided ? (
                    <Badge tone={LEDGER_ROW_TONE.cancelled} dot>
                      cancelled
                    </Badge>
                  ) : null}
                  {m.effective && (
                    <span className="d-faint" data-testid="movement-effective">
                      {m.effective.correction_id ? 'corrected' : 'limits re-run'}
                      {m.effective.category && m.effective.category !== m.category
                        ? ` · ${categoryLabel(m.effective.category)}`
                        : ''}
                      {m.effective.receipt_ref && m.effective.receipt_ref !== m.receipt_ref
                        ? ` · receipt ${m.effective.receipt_ref}`
                        : ''}
                    </span>
                  )}
                  {sh.status === 'closed' &&
                    (m.kind === 'pay_in' || m.kind === 'pay_out') &&
                    !m.voided && (
                      <Button
                        size="sm"
                        disabled={!online}
                        onClick={() =>
                          setTarget({ kind: m.kind as 'pay_in' | 'pay_out', movement: m })
                        }
                        data-testid="movement-correct"
                      >
                        Correct
                      </Button>
                    )}
                </div>
              </div>
            ))}
            {d.movements.length === 0 && <EmptyState title="No cash entries." />}
          </div>
        </section>
      </div>

      <section className="rv-section" aria-label="Corrections">
        <SectionHead title="Corrections" />
        <CorrectionsSection detail={d} target={target} onTarget={setTarget} />
      </section>

      <section className="rv-section" aria-label="Payments">
        <SectionHead title="Payments" />
        <PaymentsSection detail={d} onTarget={setTarget} />
      </section>

      <section className="rv-section" aria-label="Non-cash payments">
        <SectionHead title="Non-cash payments" />
        <NoncashRows rows={d.noncash} />
      </section>

      {(d.acks.length > 0 || d.reviews.length > 0) && (
        <section className="rv-section" aria-label="History">
          <SectionHead title="History" />
          <div className="d-list" data-testid="shift-history">
            {d.reviews.map((r) => (
              <div key={r.id} className="d-row rv-row--compact">
                <div className="d-row__main">
                  <div className="d-row__title">
                    Marked reviewed{r.via === 'day' ? ' with the day' : ''}
                  </div>
                  <div className="d-row__meta">
                    {f.at(r.at)} · {r.by.name}
                  </div>
                </div>
              </div>
            ))}
            {d.acks.map((a) => (
              <div key={a.id} className="d-row rv-row--compact">
                <div className="d-row__main">
                  <div className="d-row__title">
                    {titles.get(a.item_key) ?? ACTION_LABEL[a.kind] ?? a.kind.replace(/_/g, ' ')} ·{' '}
                    {a.verdict.replace('_', ' ')}
                  </div>
                  <div className="d-row__meta">
                    {f.at(a.at)} · {a.by.name}
                    {a.note ? ` · “${a.note}”` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
